import { makeStyles } from "@material-ui/styles";
import React, { useCallback, useContext } from "react";
import { ValueType } from "react-select/src/types";

import { useAuth } from "../../../features/auth/context";
import { OrgSiteContext } from "../../orgSiteContext";

import { SiteSelect } from "./SiteSelect";
import { OrganizationSelect } from "./OrganizationSelect";
import { DealerSelect } from "./DealerSelect";

const useStyles = (descriptive: boolean) => makeStyles({
    container: {
        marginBottom: descriptive ? undefined : "20px",
        display: "flex",
        gap: "5px",
        maxWidth: "1000px",
        flexDirection: descriptive ? "column" : "row",
        "@media (max-width: 600px)": {
            flexDirection: "column",
            gap: "12px",
        },
    },
    select: {
        minWidth: "200px",
        "@media (max-width: 600px)": {
            minWidth: 0,
            width: "100%",
        },
    },
});

interface Props {
    selectedDealerId?: number | null;
    selectedOrgId?: number | null;
    selectedSiteId?: number | null;
    orgSelectIsHidden?: boolean;
    orgSelectIsEnabled?: boolean;
    siteSelectIsHidden?: boolean;
    siteSelectIsEnabled?: boolean;
    dealerSelectIsEnabled?: boolean;
    includeAllSites?: boolean;
    bypassSiteContext?: boolean;
    onChange?: (val: {
        dealerId?: number | null;
        orgId?: number | null;
        siteId?: number | null;
    }) => void;
    descriptive?: boolean;
    ignoreOrgSiteContext?: boolean;
    isDealerRequired?: boolean;
    isOrganizationRequired?: boolean;
}

export const OrgSelect = ({
    selectedDealerId = null,
    selectedOrgId = null,
    selectedSiteId = null,
    dealerSelectIsEnabled = true,
    orgSelectIsEnabled = true,
    orgSelectIsHidden = false,
    siteSelectIsEnabled = true,
    siteSelectIsHidden = false,
    bypassSiteContext,
    onChange,
    includeAllSites = false,
    descriptive = false,
    ignoreOrgSiteContext = false,
    isDealerRequired = false,
    isOrganizationRequired = false,
}: Props) => {
    const classes = useStyles(descriptive)();

    const { isGlobalAdmin, orgId: assignedOrgId } = useAuth();
    const { dealerId, orgId, setOrgId, setSiteId, setDealerId } = useContext(OrgSiteContext);

    const handleDealerSelect = useCallback((value: ValueType<{ value: number; label: string }>) => {
        if (!onChange) return;
        const val = value as { value: number; label: string };
        onChange({
            dealerId: val ? val.value : null,
            orgId: null,
            siteId: null,
        });
        setDealerId(val ? val.value : null);
    }, [setDealerId, onChange]);

    const handleOrganizationSelect = useCallback((value: ValueType<{ value: number; label: string }>) => {
        if (!onChange) return;
        const val = value as { value: number; label: string };
        onChange({
            dealerId: selectedDealerId,
            orgId: val ? val.value : null,
            siteId: null,
        });
        setOrgId(val ? val.value : null);
    }, [setOrgId, onChange, selectedDealerId]);

    const handleSiteSelect = useCallback((value: ValueType<{ value: number; label: string }>) => {
        if (!onChange) return;
        const val = value as { value: number; label: string };
        onChange({
            dealerId: selectedDealerId,
            orgId: selectedOrgId,
            siteId: val ? val.value : null,
        });
        setSiteId(val ? val.value : null);
    }, [setSiteId, onChange, selectedOrgId, selectedDealerId]);

    return (
        <div className={classes.container}>
            <DealerSelect
                selectedDealerId={ignoreOrgSiteContext ? selectedDealerId : dealerId}
                handleChange={handleDealerSelect}
                showLabel={descriptive}
                fullWidth={descriptive}
                canGetOptions={isGlobalAdmin}
                disabled={!isGlobalAdmin || !dealerSelectIsEnabled}
                className={classes.select}
                required={isDealerRequired}
            />

            {!orgSelectIsHidden && (
                <OrganizationSelect
                    selectedOrganizationId={ignoreOrgSiteContext ? selectedOrgId : orgId}
                    handleChange={handleOrganizationSelect}
                    dealerId={selectedDealerId || null}
                    showLabel={descriptive}
                    fullWidth={descriptive}
                    disabled={!orgSelectIsEnabled || !!assignedOrgId}
                    className={classes.select}
                    required={isOrganizationRequired}
                />
            )}

            {!siteSelectIsHidden && (
                <SiteSelect
                    selectedSiteId={selectedSiteId || null}
                    handleChange={handleSiteSelect}
                    organizationId={selectedOrgId || null}
                    showOptionAll={includeAllSites}
                    showLabel={descriptive}
                    fullWidth={true}
                    disabled={!siteSelectIsEnabled || (!!assignedOrgId && !bypassSiteContext)}
                    className={classes.select}
                />
            )}
        </div>
    );
};
